import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../providers/authentication-provider';

// TypeScript declaration for the global saltfish object.
// Adjust the return types of init/startPlaylist if they return something specific.
declare global {
    interface Window {
        saltfish?: {
            init: (config: { token: string }) => void;
            startPlaylist: (playlistId: string) => void;
            on: (event: 'initialized', callback: () => void) => void;
            off: (event: 'initialized', callback: () => void) => void;
            identify: (userId: string) => void;
        };
    }
}

/**
 * Custom hook to interact with the Saltfish library.
 * Assumes the Saltfish library script is loaded globally (e.g., via index.html).
 */
export function useSaltfish() {
    // const [isInitialized, setIsInitialized] = useState(false); // Removed
    const [isInitializing, setIsInitializing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const initializedEventHandlerRef = useRef<(() => void) | null>(null);
    const {
        authState: { userId },
    } = useAuth();

    const isInitializedRef = useRef(false); // Initialize directly
    // useEffect(() => { // Removed effect that synced isInitialized state to ref
    //     isInitializedRef.current = isInitialized;
    // }, [isInitialized]);

    /**
     * Initializes the Saltfish library with the provided token.
     */
    const initSaltfish = useCallback(
        (token: string): void => {
            if (!window.saltfish) {
                const errMsg = 'Saltfish library is not available on window.';
                console.error(errMsg);
                setError(errMsg);
                return;
            }

            if (isInitializedRef.current || isInitializing) {
                console.warn(`Saltfish is already ${isInitializedRef.current ? 'initialized' : 'initializing'}.`);
                return;
            }

            setIsInitializing(true);
            setError(null);
            console.log('Initializing Saltfish...');

            const handleInitialized = () => {
                console.log('Saltfish: initialized');
                isInitializedRef.current = true; // Set ref directly
                // setIsInitialized(true); // Removed
                setIsInitializing(false);
                if (window.saltfish && initializedEventHandlerRef.current) {
                    window.saltfish.off('initialized', initializedEventHandlerRef.current);
                    initializedEventHandlerRef.current = null;
                }
            };

            initializedEventHandlerRef.current = handleInitialized;
            window.saltfish.on('initialized', handleInitialized);

            try {
                window.saltfish.init({ token });
            } catch (e) {
                const errMsg =
                    e instanceof Error ? e.message : 'An unknown error occurred during Saltfish initialization.';
                console.error('Saltfish initialization failed:', e);
                setError(errMsg);
                // setIsInitialized(false); // Removed - ref remains false if init fails before handleInitialized
                setIsInitializing(false); // Stop initializing state on error
                if (window.saltfish && initializedEventHandlerRef.current) {
                    window.saltfish.off('initialized', initializedEventHandlerRef.current);
                    initializedEventHandlerRef.current = null;
                }
            }
        },
        [isInitializing] // Depends on isInitializing state and stable setters/refs
    );

    // Effect for unmount cleanup
    useEffect(() => {
        return () => {
            if (window.saltfish && initializedEventHandlerRef.current) {
                console.log('Cleaning up Saltfish "initialized" listener on unmount.');
                window.saltfish.off('initialized', initializedEventHandlerRef.current);
                initializedEventHandlerRef.current = null;
            }
        };
    }, []); // Empty dependency array: runs on mount, cleans up on unmount

    const _startSaltfishPlaylist = useCallback(
        (playlistId: string): void => {
            if (!isInitializedRef.current) {
                const errMsg = 'Saltfish is not initialized. Call initSaltfish first.';
                console.error(errMsg);
                // setError(errMsg); // User had this commented out here
                return;
            }

            if (!window.saltfish) {
                const errMsg = 'Saltfish library became unavailable after initialization.';
                console.error(errMsg);
                setError(errMsg);
                return;
            }

            try {
                console.log(`Identifying user in Saltfish: ${userId}`);
                window.saltfish.identify(userId);
                console.log(`Starting Saltfish playlist: ${playlistId}`);
                window.saltfish.startPlaylist(playlistId);
            } catch (e) {
                const errMsg = e instanceof Error ? e.message : 'An unknown error occurred starting Saltfish playlist.';
                console.error('Saltfish startPlaylist failed:', e);
                setError(errMsg);
            }
        },
        [userId]
    );

    const saltfishToken = 'Hk2ZceqYKLNscJWskBXO';

    useEffect(() => {
        // Log current status for debugging
        console.log('Saltfish Ref: isInitialized', isInitializedRef.current);
        console.log('Saltfish State: isInitializing', isInitializing);

        if (saltfishToken && !isInitializedRef.current && !isInitializing) {
            console.log('Saltfish: Triggering Initialization...');
            initSaltfish(saltfishToken);
        }
    }, [saltfishToken, isInitializing, initSaltfish]); // Depends on isInitializing to re-evaluate

    const startSaltfishPlaylist = useCallback(
        (playlistId: string) => {
            if (isInitializedRef.current) {
                _startSaltfishPlaylist(playlistId);
            } else {
                if (window.saltfish && typeof window.saltfish.on === 'function') {
                    console.log('Saltfish not initialized. Queuing playlist start for "initialized" event.');
                    const playOnInitialized = () => {
                        console.log(`Saltfish "initialized" event received. Starting playlist: ${playlistId}`);
                        _startSaltfishPlaylist(playlistId);
                        if (window.saltfish && typeof window.saltfish.off === 'function') {
                            window.saltfish.off('initialized', playOnInitialized);
                        }
                    };
                    window.saltfish.on('initialized', () => setTimeout(playOnInitialized, 1));
                } else {
                    console.error(
                        'Saltfish library or .on method not available to queue playlist start. Playlist will not start.'
                    );
                    setError('Saltfish library not available to respond to initialization for starting playlist.');
                }
            }
        },
        [_startSaltfishPlaylist]
    );

    return {
        error,
        startSaltfishPlaylist,
    };
}
